import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../../theme/tokens';
import { useGitHubUsername } from '../hooks/useGitHubUsername';
import {
  useMyPullRequests,
  type PrStatus,
  type PullRequest,
} from '../hooks/useMyPullRequests';

const statusConfig: Record<
  PrStatus,
  { label: string; color: string; bg: string }
> = {
  CHANGES_REQUESTED: {
    label: 'CHANGES REQUESTED',
    color: '#fff',
    bg: '#e67e22',
  },
  AWAITING_REVIEW: { label: 'AWAITING REVIEW', color: '#fff', bg: '#3498db' },
  APPROVED: { label: 'APPROVED', color: '#fff', bg: '#27ae60' },
  DRAFT: { label: 'DRAFT', color: '#fff', bg: '#95a5a6' },
};

const timeAgo = (dateStr: string): string => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

const COLLAPSED_COUNT = 2;
const MAX_EXPANDED = 10;

const PrRow = ({
  pr,
  showAuthor,
}: {
  pr: PullRequest;
  showAuthor?: boolean;
}) => {
  const theme = useTheme();
  const config = statusConfig[pr.status];

  return (
    <Link
      href={pr.url}
      target="_blank"
      rel="noreferrer"
      underline="none"
      color="inherit"
      sx={{
        display: 'block',
        px: 2.5,
        py: 1.5,
        transition: 'background-color 150ms ease',
        '&:hover': {
          backgroundColor: getSurfaceColor(theme, 'subtle'),
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Chip
          label={config.label}
          size="small"
          sx={{
            backgroundColor: config.bg,
            borderRadius: '4px',
            color: config.color,
            fontSize: '0.65rem',
            fontWeight: 700,
            height: 20,
            letterSpacing: '0.03em',
          }}
        />
        <Typography variant="caption" color="text.secondary">
          #{pr.number}
        </Typography>
        {showAuthor && pr.author && (
          <Typography variant="caption" color="text.secondary">
            {pr.author}
          </Typography>
        )}
        {pr.teamAssigned && (
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontStyle: 'italic' }}
          >
            via team
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {timeAgo(pr.updatedAt)}
        </Typography>
      </Box>
      <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
        {pr.title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {pr.repo}
      </Typography>
    </Link>
  );
};

const PrSection = ({
  title,
  subtitle,
  prs,
  viewAllHref,
  showAuthor,
}: {
  title: string;
  subtitle: string;
  prs: PullRequest[];
  viewAllHref?: string;
  showAuthor?: boolean;
}) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const [expanded, setExpanded] = useState(false);

  if (!prs.length) return null;

  const collapsed = prs.slice(0, COLLAPSED_COUNT);
  const expandable = prs.slice(COLLAPSED_COUNT, MAX_EXPANDED);
  const hasMore = prs.length > MAX_EXPANDED;

  return (
    <Box>
      <Box
        onClick={expandable.length ? () => setExpanded(v => !v) : undefined}
        sx={{
          backgroundColor:
            theme.palette.mode === 'light'
              ? '#FBFCFC'
              : getSurfaceColor(theme, 'subtle'),
          borderBottom: `1px solid ${getSurfaceBorder(theme)}`,
          borderTop: `1px solid ${getSurfaceBorder(theme)}`,
          cursor: expandable.length ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          px: 2.5,
          py: 1,
          userSelect: 'none',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: tokens.textMuted,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: tokens.textMuted, fontWeight: 400 }}
            >
              {prs.length}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: tokens.textMuted, fontWeight: 400, lineHeight: 1.3 }}
          >
            {subtitle}
          </Typography>
        </Box>
        {expandable.length > 0 && (
          <ExpandMoreIcon
            sx={{
              color: tokens.textMuted,
              fontSize: 18,
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 200ms ease',
            }}
          />
        )}
      </Box>
      <Stack
        divider={
          <Box
            sx={{
              height: '1px',
              backgroundColor: theme.palette.divider,
              mx: 2.5,
            }}
          />
        }
        spacing={0}
      >
        {collapsed.map(pr => (
          <PrRow key={pr.id} pr={pr} showAuthor={showAuthor} />
        ))}
      </Stack>
      {expandable.length > 0 && (
        <Collapse in={expanded}>
          <Stack
            divider={
              <Box
                sx={{
                  height: '1px',
                  backgroundColor: theme.palette.divider,
                  mx: 2.5,
                }}
              />
            }
            spacing={0}
          >
            {expandable.map(pr => (
              <PrRow key={pr.id} pr={pr} showAuthor={showAuthor} />
            ))}
          </Stack>
        </Collapse>
      )}
      {hasMore && viewAllHref && (
        <Box sx={{ px: 2.5, mt: 1 }}>
          <Link
            href={viewAllHref}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            variant="caption"
            sx={{ fontWeight: 600 }}
          >
            View all {prs.length}+ on GitHub →
          </Link>
        </Box>
      )}
    </Box>
  );
};

export const MyPullRequests = () => {
  const theme = useTheme();
  const { value: username, loading: loadingUser } = useGitHubUsername();
  const { value: sections, loading: loadingPrs } = useMyPullRequests(username);

  const loading = loadingUser || loadingPrs;

  if (!loadingUser && !username) return null;

  const totalCount =
    (sections?.authored.length ?? 0) +
    (sections?.review.length ?? 0) +
    (sections?.team.length ?? 0);

  return (
    <Paper sx={{ px: 0, py: 3 }}>
      <Box sx={{ px: 3, mb: 1 }}>
        <Typography variant="h6">Pull Requests</Typography>
      </Box>

      {loading ? (
        <Stack spacing={1} sx={{ px: 3 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={64} />
          ))}
        </Stack>
      ) : totalCount === 0 ? (
        <Box sx={{ px: 3 }}>
          <Box
            sx={{
              backgroundColor: getSurfaceColor(theme, 'subtle'),
              border: `1px solid ${getSurfaceBorder(theme)}`,
              borderRadius: `${radii.md}px`,
              px: 3,
              py: 3,
              textAlign: 'center',
            }}
          >
            <Typography color="text.secondary" variant="body2">
              No open PRs — nice!
            </Typography>
          </Box>
        </Box>
      ) : (
        <Stack spacing={0}>
          <PrSection
            title="Your PRs"
            subtitle="Pull requests you created"
            prs={sections!.authored}
            viewAllHref={`https://github.com/pulls?q=is:open+is:pr+author:${username}+org:meltwater`}
          />
          <PrSection
            title="Assigned to you"
            subtitle="Review requests for you or your team"
            prs={sections!.review}
            viewAllHref={`https://github.com/pulls?q=is:open+is:pr+review-requested:${username}+org:meltwater`}
            showAuthor
          />
          <PrSection
            title="Your team's PRs"
            subtitle="Open PRs on your team's repositories"
            prs={sections!.team}
            showAuthor
          />
        </Stack>
      )}
    </Paper>
  );
};
